import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Truck, PackageCheck } from "lucide-react";

const STATUSES = ["scheduled", "loaded", "in_transit", "delivered", "returned"];
const statusColor = (s) =>
  s === "delivered" ? "bg-emerald-100 text-emerald-800" :
  s === "in_transit" ? "bg-blue-100 text-blue-800" :
  s === "loaded" ? "bg-indigo-100 text-indigo-800" :
  s === "returned" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700";

export function MfgDispatchTab() {
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rows, setRows] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const loadRefs = async () => {
    try {
      const [b, c] = await Promise.all([api.get("/branches"), api.get("/customers")]);
      setBranches(b.data); setCustomers(c.data);
    } catch { /* */ }
  };
  useEffect(() => { loadRefs(); }, []);

  const load = async () => {
    const params = new URLSearchParams();
    if (branchFilter) params.set("branch_id", branchFilter);
    if (statusFilter) params.set("delivery_status", statusFilter);
    try {
      const q = params.toString() ? `?${params.toString()}` : "";
      const r = await api.get(`/mfg/dispatch${q}`);
      setRows(r.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchFilter, statusFilter]);

  const branchName = (id) => (branches.find((b) => b.id === id) || {}).name || id;
  const custName = (id) => (customers.find((c) => c.id === id) || {}).name || "—";

  const save = async () => {
    if (!form.branch_id) { toast.error("Factory is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (form.id) {
        const { id, branch_id, dispatch_no, invoice_id, customer_id, items, created_at, created_by, updated_at, ...body } = payload;
        await api.patch(`/mfg/dispatch/${id}`, body);
      } else {
        await api.post("/mfg/dispatch", payload);
      }
      toast.success("Saved"); setOpen(false); setForm({}); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const patchStatus = async (row, newStatus) => {
    try { await api.patch(`/mfg/dispatch/${row.id}`, { delivery_status: newStatus }); toast.success(`Marked ${newStatus.replace("_", " ")}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete dispatch?")) return;
    try { await api.delete(`/mfg/dispatch/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="pt-3 space-y-4" data-testid="mfg-dispatch-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Factory</Label>
          <Select value={branchFilter} onValueChange={(v) => setBranchFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="mfg-dispatch-branch-filter"><SelectValue placeholder="All factories" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">All factories</SelectItem>
              {branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Status</Label>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="mfg-dispatch-status-filter"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">Any status</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" className="bg-[#C05621] text-white ml-auto self-end" onClick={() => { setForm({ delivery_status: "scheduled", branch_id: branchFilter || undefined, items: [] }); setOpen(true); }} data-testid="mfg-dispatch-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Dispatch
        </Button>
      </div>

      <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="mfg-dispatch-rows">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Dispatch #</th>
              <th className="text-left px-3 py-2">Factory</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Vehicle</th>
              <th className="text-left px-3 py-2">Driver</th>
              <th className="text-left px-3 py-2">Destination</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Dispatched</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7]" data-testid={`mfg-dispatch-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.dispatch_no}</td>
                <td className="px-3 py-2 text-xs">{branchName(r.branch_id)}</td>
                <td className="px-3 py-2 text-xs">{r.customer_id ? custName(r.customer_id) : "—"}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.vehicle_no || "—"}<div className="text-[10px] text-[#8B7355]">{r.transport_company || ""}</div></td>
                <td className="px-3 py-2 text-xs">{r.driver_name || "—"}<div className="text-[10px] text-[#8B7355]">{r.driver_phone || ""}</div></td>
                <td className="px-3 py-2 text-xs">{r.destination || "—"}</td>
                <td className="px-3 py-2 text-center"><Badge className={`border-0 ${statusColor(r.delivery_status)}`}>{r.delivery_status?.replace("_", " ")}</Badge></td>
                <td className="px-3 py-2 text-xs">{r.dispatch_time ? r.dispatch_time.slice(0, 16).replace("T", " ") : "—"}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.delivery_status === "scheduled" && (
                    <Button size="sm" variant="outline" className="text-indigo-700 border-indigo-300" onClick={() => patchStatus(r, "loaded")} data-testid={`mfg-dispatch-load-${r.id}`}>Load</Button>
                  )}
                  {r.delivery_status === "loaded" && (
                    <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 ml-1" onClick={() => patchStatus(r, "in_transit")} data-testid={`mfg-dispatch-transit-${r.id}`}>
                      <Truck className="w-3 h-3 mr-1" /> Depart
                    </Button>
                  )}
                  {r.delivery_status === "in_transit" && (
                    <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 ml-1" onClick={() => patchStatus(r, "delivered")} data-testid={`mfg-dispatch-deliver-${r.id}`}>
                      <PackageCheck className="w-3 h-3 mr-1" /> Deliver
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="ml-1" onClick={() => { setForm(r); setOpen(true); }}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={() => del(r.id)}>×</Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12 text-[#8B7355]">
                <Truck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No dispatches yet — click <b>New Dispatch</b> to log a truck-out.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-[Outfit]">{form.id ? "Edit" : "New"} Dispatch</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label>Factory*</Label>
              <Select value={form.branch_id || ""} onValueChange={(v) => setForm({ ...form, branch_id: v })} disabled={!!form.id}>
                <SelectTrigger data-testid="mfg-dispatch-form-branch"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Customer (optional)</Label>
              <Select value={form.customer_id || ""} onValueChange={(v) => setForm({ ...form, customer_id: v === "__none" ? "" : v })} disabled={!!form.id}>
                <SelectTrigger data-testid="mfg-dispatch-form-customer"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72 overflow-y-auto"><SelectItem value="__none">—</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Vehicle #</Label>
              <Input value={form.vehicle_no || ""} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} placeholder="MH-01-AB-1234" data-testid="mfg-dispatch-form-vehicle" />
            </div>
            <div><Label>Transport Company</Label>
              <Input value={form.transport_company || ""} onChange={(e) => setForm({ ...form, transport_company: e.target.value })} />
            </div>
            <div><Label>Driver Name</Label>
              <Input value={form.driver_name || ""} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="mfg-dispatch-form-driver" />
            </div>
            <div><Label>Driver Phone</Label>
              <Input value={form.driver_phone || ""} onChange={(e) => setForm({ ...form, driver_phone: e.target.value })} />
            </div>
            <div><Label>Dispatch time</Label>
              <Input type="datetime-local" value={(form.dispatch_time || "").slice(0, 16)} onChange={(e) => setForm({ ...form, dispatch_time: e.target.value })} />
            </div>
            <div><Label>Destination</Label>
              <Input value={form.destination || ""} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="Main Store / Customer Address" />
            </div>
            <div className="col-span-2"><Label>POD URL (Proof of Delivery)</Label>
              <Input value={form.pod_url || ""} onChange={(e) => setForm({ ...form, pod_url: e.target.value })} placeholder="https://…" />
            </div>
            <div className="col-span-2"><Label>Status</Label>
              <Select value={form.delivery_status || "scheduled"} onValueChange={(v) => setForm({ ...form, delivery_status: v })}>
                <SelectTrigger data-testid="mfg-dispatch-form-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Notes</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#2C1810] text-white" data-testid="mfg-dispatch-form-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
